import { PLACES_FUNCTION_PATH, PLACES_PROTOCOL_VERSION, checkPlacesResponse, type AddressSuggestion, type ResolvedAddress } from '../../supabase/functions/_shared/placesProtocol';
import { currentAccessToken } from './aiClient';

// Client transport to the address-completion function (supabase/functions/places-v2). Same
// discipline as src/backend/billingClient.ts: the request carries the signed-in user's JWT, no
// Google key exists in this bundle, every outcome is a code the form can act on, and a raw server
// message is never shown to an inspector. Address completion is a convenience: when it is not
// there (offline, not configured, not signed in) the address is typed by hand and nothing is lost.
export type PlacesFailureCode = 'offline' | 'network' | 'timeout' | 'notConfigured' | 'unauthenticated' | 'upstream' | 'malformedResponse' | 'serverError';

export type PlacesOutcome<T> = { kind: 'ok'; value: T } | { kind: 'failed'; code: PlacesFailureCode; message: string };

export interface PlacesTransport {
  autocomplete(token: string | null, body: { input: string; sessionToken: string }): Promise<PlacesOutcome<AddressSuggestion[]>>;
  details(token: string | null, body: { placeId: string; sessionToken: string }): Promise<PlacesOutcome<ResolvedAddress>>;
}

export const PLACES_CLIENT_TIMEOUT_MS = 8_000;

export function placesEndpoint(): string | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  return base.length > 0 ? `${base}${PLACES_FUNCTION_PATH}` : null;
}

function online(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

async function call(body: Record<string, unknown>, token: string | null): Promise<PlacesOutcome<unknown>> {
  const endpoint = placesEndpoint();
  const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
  if (!endpoint || anon.length === 0) return { kind: 'failed', code: 'notConfigured', message: 'address completion is not configured' };
  if (!online()) return { kind: 'failed', code: 'offline', message: 'device is offline' };
  if (!token) return { kind: 'failed', code: 'unauthenticated', message: 'no session token' };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PLACES_CLIENT_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ protocolVersion: PLACES_PROTOCOL_VERSION, ...body }),
      signal: controller.signal
    });
  } catch (raw) {
    clearTimeout(timer);
    const aborted = raw instanceof Error && raw.name === 'AbortError';
    return { kind: 'failed', code: aborted ? 'timeout' : 'network', message: aborted ? 'address request timed out' : 'network error' };
  }
  clearTimeout(timer);
  let parsed: unknown = null;
  try {
    parsed = (await res.json()) as unknown;
  } catch {
    parsed = null;
  }
  const checked = checkPlacesResponse(parsed);
  if (!checked) return { kind: 'failed', code: res.status === 404 ? 'notConfigured' : 'malformedResponse', message: `unexpected answer (HTTP ${res.status})` };
  if (!checked.ok) {
    if (checked.code === 'unauthenticated') return { kind: 'failed', code: 'unauthenticated', message: checked.message };
    if (checked.code === 'notConfigured') return { kind: 'failed', code: 'notConfigured', message: checked.message };
    if (checked.code === 'upstream') return { kind: 'failed', code: 'upstream', message: checked.message };
    return { kind: 'failed', code: 'serverError', message: checked.message };
  }
  return { kind: 'ok', value: checked };
}

const fetchTransport: PlacesTransport = {
  async autocomplete(token, body) {
    const outcome = await call({ action: 'autocomplete', ...body }, token);
    if (outcome.kind === 'failed') return outcome;
    const value = outcome.value as { action: string; suggestions?: AddressSuggestion[] };
    return value.action === 'autocomplete' && value.suggestions ? { kind: 'ok', value: value.suggestions } : { kind: 'failed', code: 'malformedResponse', message: 'wrong action in answer' };
  },
  async details(token, body) {
    const outcome = await call({ action: 'details', ...body }, token);
    if (outcome.kind === 'failed') return outcome;
    const value = outcome.value as { action: string; address?: ResolvedAddress };
    return value.action === 'details' && value.address ? { kind: 'ok', value: value.address } : { kind: 'failed', code: 'malformedResponse', message: 'wrong action in answer' };
  }
};

let transportForTests: PlacesTransport | null = null;

export function setPlacesTransportForTests(transport: PlacesTransport | null): void {
  transportForTests = transport;
}

export function placesTransport(): PlacesTransport {
  return transportForTests ?? fetchTransport;
}

// One call each, with the signed-in user's token (null → unauthenticated, handled by the transport).
export async function suggestAddresses(input: string, sessionToken: string): Promise<PlacesOutcome<AddressSuggestion[]>> {
  return placesTransport().autocomplete(await currentAccessToken(), { input, sessionToken });
}

export async function resolveAddress(placeId: string, sessionToken: string): Promise<PlacesOutcome<ResolvedAddress>> {
  return placesTransport().details(await currentAccessToken(), { placeId, sessionToken });
}
